/**
 * 掲示板関連のTanStack Query hooks
 *
 * カスタムフック群：
 * - useBulletinBoards: 掲示板一覧取得
 * - useBulletinBoardDetail: 掲示板詳細取得
 * - useCreateBulletinBoard: 掲示板作成
 * - useUpdateBulletinBoard: 掲示板更新
 * - useDeleteBulletinBoard: 掲示板削除
 * - useBulletinBoardComments: 掲示板コメント一覧取得
 * - useCreateBulletinBoardComment: コメント作成
 * - useDeleteBulletinBoardComment: コメント削除
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  getBulletinBoards,
  getBulletinBoard,
  createBulletinBoard,
  updateBulletinBoard,
  deleteBulletinBoard,
  getBulletinComments,
  createBulletinComment,
  deleteBulletinComment,
  getBulletinStatistics,
  getBulletinCategories,
  getBulletinPriorities,
  markAsRead,
} from './index';
import type {
  BulletinBoardListParams,
  BulletinBoardFormData,
  BulletinCommentFormData,
} from '../types';

const transformV3ApiResponse = (apiResponse: any) => ({
  id: apiResponse.id,
  title: apiResponse.title,
  content: apiResponse.body || '',
  category: apiResponse.category?.value || apiResponse.category || '',
  category_label: apiResponse.category?.label || '',
  priority: apiResponse.priority?.value || apiResponse.priority || 'normal',
  priority_label: apiResponse.priority?.label || '',
  priority_color: apiResponse.priority?.color || 'blue',
  store_id: apiResponse.store_id || null,
  store: apiResponse.store,
  author_id: apiResponse.author_id,
  author: apiResponse.author,
  is_pinned: apiResponse.flags?.is_pinned ?? false,
  is_active: apiResponse.flags?.is_active ?? true,
  is_read_by_user: apiResponse.flags?.is_read ?? false,
  publish_start: apiResponse.publish_window?.start || null,
  publish_end: apiResponse.publish_window?.end || null,
  view_count: apiResponse.statistics?.view_count ?? 0,
  read_logs_count: apiResponse.statistics?.read_count ?? 0,
  active_comments_count: apiResponse.statistics?.comment_count ?? 0,
  file_paths: apiResponse.attachments || [],
  created_at: apiResponse.meta?.created_at || null,
  updated_at: apiResponse.meta?.updated_at || null,
  deleted_at: apiResponse.meta?.deleted_at || null,
  status: 'published' as const,
  is_published: true,
  is_expired: false,
  days_since_published: null,
});

export const bulletinBoardKeys = {
  all: ['bulletinBoards'] as const,
  lists: () => [...bulletinBoardKeys.all, 'list'] as const,
  list: (params: BulletinBoardListParams) => [...bulletinBoardKeys.lists(), params] as const,
  details: () => [...bulletinBoardKeys.all, 'detail'] as const,
  detail: (id: number) => [...bulletinBoardKeys.details(), id] as const,
  comments: (bulletinId: number) => [...bulletinBoardKeys.detail(bulletinId), 'comments'] as const,
  commentsList: (bulletinId: number, params?: any) =>
    [...bulletinBoardKeys.comments(bulletinId), params] as const,
} as const;

/**
 * 掲示板一覧取得フック
 */
export const useBulletinBoards = (params: BulletinBoardListParams) => {
  return useQuery({
    queryKey: bulletinBoardKeys.list(params),
    queryFn: () => getBulletinBoards(params),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    select: (response: any) => ({
      ...response,
      data: response.data?.map(transformV3ApiResponse) || [],
    }),
  });
};

/**
 * 掲示板詳細取得フック（自動既読マーク付き）
 */
export const useBulletinBoardDetail = (id: number) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: bulletinBoardKeys.detail(id),
    queryFn: async () => {
      const data = await getBulletinBoard(id);

      // 詳細を取得したら自動的に既読マーク
      try {
        await markAsRead(id);

        // 統計キャッシュを即座に無効化（未読数を更新）
        queryClient.invalidateQueries({
          queryKey: ['bulletinBoards', 'statistics'],
        });
      } catch (error) {
        console.warn('既読マークに失敗しましたが、詳細は表示します', error);
      }

      return data;
    },
    enabled: id > 0,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,

    select: (apiResponse: any) => {
      const transformed = transformV3ApiResponse(apiResponse);
      return {
        ...transformed,
        formattedPublishStart: transformed.publish_start
          ? new Date(transformed.publish_start).toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '公開日時未設定',
        hasAttachments: transformed.file_paths && transformed.file_paths.length > 0,
        canEdit: true,
        canDelete: true,
        canComment: true,
      };
    },
  });
};

/**
 * 掲示板作成フック
 */
export const useCreateBulletinBoard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulletinBoardFormData) => createBulletinBoard(data),
    onSuccess: (apiData) => {
      const transformed = transformV3ApiResponse(apiData);

      // 一覧キャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: bulletinBoardKeys.lists(),
      });

      // 統計キャッシュも無効化（新規掲示板が未読として反映されるように）
      queryClient.invalidateQueries({
        queryKey: ['bulletinBoards', 'statistics'],
      });

      notifications.show({
        title: '作成完了',
        message: '掲示板を作成しました',
        color: 'green',
      });

      return transformed;
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || '掲示板の作成に失敗しました';
      notifications.show({
        title: '作成エラー',
        message,
        color: 'red',
      });
    },
  });
};

/**
 * 掲示板更新フック
 */
export const useUpdateBulletinBoard = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulletinBoardFormData) => updateBulletinBoard(id, data),
    onSuccess: (apiData) => {
      const transformed = transformV3ApiResponse(apiData);

      queryClient.setQueryData(bulletinBoardKeys.detail(id), transformed);

      // 一覧キャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: bulletinBoardKeys.lists(),
      });

      // 統計キャッシュも無効化
      queryClient.invalidateQueries({
        queryKey: ['bulletinBoards', 'statistics'],
      });

      notifications.show({
        title: '更新完了',
        message: '掲示板を更新しました',
        color: 'green',
      });

      return transformed;
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || '掲示板の更新に失敗しました';
      notifications.show({
        title: '更新エラー',
        message,
        color: 'red',
      });
    },
  });
};

/**
 * 掲示板削除フック
 */
export const useDeleteBulletinBoard = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteBulletinBoard(id),
    onSuccess: (_, deletedId) => {
      // 詳細キャッシュを削除
      queryClient.removeQueries({
        queryKey: bulletinBoardKeys.detail(deletedId),
      });

      // 一覧キャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: bulletinBoardKeys.lists(),
      });

      // 統計キャッシュも無効化
      queryClient.invalidateQueries({
        queryKey: ['bulletinBoards', 'statistics'],
      });

      notifications.show({
        title: '削除完了',
        message: '掲示板を削除しました',
        color: 'green',
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || '掲示板の削除に失敗しました';
      notifications.show({
        title: '削除エラー',
        message,
        color: 'red',
      });
    },
  });
};

/**
 * 掲示板コメント一覧取得フック
 */
export const useBulletinBoardComments = (
  bulletinId: number,
  params?: any
) => {
  return useQuery({
    queryKey: bulletinBoardKeys.commentsList(bulletinId, params),
    queryFn: () => getBulletinComments(bulletinId, params),
    enabled: bulletinId > 0,
    staleTime: 30 * 1000, // 30秒間キャッシュ
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * 掲示板コメント作成フック
 */
export const useCreateBulletinBoardComment = (bulletinId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulletinCommentFormData) =>
      createBulletinComment(bulletinId, data),
    onSuccess: () => {
      // コメント一覧キャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: bulletinBoardKeys.comments(bulletinId),
      });

      // 掲示板詳細のコメント数を更新するため詳細も無効化
      queryClient.invalidateQueries({
        queryKey: bulletinBoardKeys.detail(bulletinId),
      });

      notifications.show({
        title: 'コメント投稿完了',
        message: 'コメントを投稿しました',
        color: 'green',
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'コメントの投稿に失敗しました';
      notifications.show({
        title: 'コメント投稿エラー',
        message,
        color: 'red',
      });
    },
  });
};

/**
 * 掲示板コメント削除フック
 */
export const useDeleteBulletinBoardComment = (bulletinId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (commentId: number) => deleteBulletinComment(bulletinId, commentId),
    onSuccess: () => {
      // コメント一覧キャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: bulletinBoardKeys.comments(bulletinId),
      });

      // 掲示板詳細のコメント数を更新するため詳細も無効化
      queryClient.invalidateQueries({
        queryKey: bulletinBoardKeys.detail(bulletinId),
      });

      notifications.show({
        title: 'コメント削除完了',
        message: 'コメントを削除しました',
        color: 'green',
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || 'コメントの削除に失敗しました';
      notifications.show({
        title: 'コメント削除エラー',
        message,
        color: 'red',
      });
    },
  });
};

/**
 * 掲示板既読マークフック
 */
export const useMarkBulletinAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bulletinId: number) => markAsRead(bulletinId),
    onSuccess: (response, bulletinId) => {
      // バックエンドから返された最新の未読数を使って統計キャッシュを即座に更新
      if (response.unread_count !== undefined) {
        queryClient.setQueryData(['bulletinBoards', 'statistics'], (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            unread_bulletins: response.unread_count,
          };
        });
      }

      // 掲示板詳細キャッシュを無効化
      queryClient.invalidateQueries({
        queryKey: bulletinBoardKeys.detail(bulletinId),
      });

      // 統計キャッシュも念のため無効化（次回フェッチ時に最新データを取得）
      queryClient.invalidateQueries({
        queryKey: ['bulletinBoards', 'statistics'],
      });

      // 一覧キャッシュも無効化（is_read_by_userフラグ更新）
      queryClient.invalidateQueries({
        queryKey: bulletinBoardKeys.lists(),
      });

      notifications.show({
        title: '既読マーク完了',
        message: '既読マークしました',
        color: 'green',
      });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || '既読マークに失敗しました';
      notifications.show({
        title: '既読マークエラー',
        message,
        color: 'red',
      });
    },
  });
};

// ========== Export Aliases for Backward Compatibility ==========
export { useBulletinBoards as useBulletinBoardList };
export { useCreateBulletinBoard as useBulletinBoardCreate };
export { useUpdateBulletinBoard as useBulletinBoardUpdate };
export { useDeleteBulletinBoard as useBulletinBoardDelete };
export { useBulletinBoardComments as useBulletinComments };
export { useCreateBulletinBoardComment as useBulletinCommentCreate };
export { useDeleteBulletinBoardComment as useBulletinCommentDelete };

// Statistics/Options hooks
export const useBulletinStatistics = () => {
  return useQuery({
    queryKey: ['bulletinBoards', 'statistics'],
    queryFn: () => getBulletinStatistics(),
    staleTime: 5 * 60 * 1000, // 5分キャッシュ
    gcTime: 10 * 60 * 1000,
  });
};

export const useBulletinCategories = () => {
  return useQuery({
    queryKey: ['bulletinBoards', 'options', 'categories'],
    queryFn: () => getBulletinCategories(),
    staleTime: 30 * 60 * 1000, // 30分キャッシュ
  });
};

export const useBulletinPriorities = () => {
  return useQuery({
    queryKey: ['bulletinBoards', 'options', 'priorities'],
    queryFn: () => getBulletinPriorities(),
    staleTime: 30 * 60 * 1000, // 30分キャッシュ
  });
};
